/** Debe coincidir con `LOAD_PRODUCTS_UPLOAD_MAX_ASINS_PER_REQUEST` del backend (default 10 000). */
export const UPLOAD_ASINS_MAX_PER_REQUEST = 10000;

const UPLOAD_CHUNK_MAX_RETRIES = 3;
const UPLOAD_CHUNK_RETRY_DELAY_MS = 1500;

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isRetryableUploadError(error) {
  if (!error?.response) {
    return true;
  }
  const status = error.response.status;
  return status === 408 || (status >= 502 && status <= 504);
}

export function parseAsinsFromFileText(text) {
  const lines = text.split(/\r?\n/).filter((line) => line.trim() !== "");

  return lines
    .map((line) => {
      if (line.includes(",")) {
        return line.split(",")[0].trim();
      }
      if (line.includes("\t")) {
        return line.split("\t")[0].trim();
      }
      return line.trim();
    })
    .filter((asin) => asin.length > 0);
}

export function splitAsinsIntoChunks(asins, chunkSize = UPLOAD_ASINS_MAX_PER_REQUEST) {
  const chunks = [];
  for (let i = 0; i < asins.length; i += chunkSize) {
    chunks.push(asins.slice(i, i + chunkSize));
  }
  return chunks;
}

/**
 * Sube ASINs en lotes secuenciales (1 request activo por sesión).
 * Reintenta el mismo chunk de forma idempotente ante errores de red.
 */
export async function uploadAsinsInChunks({
  asins,
  categoryName,
  postChunk,
  onProgress,
}) {
  const chunks = splitAsinsIntoChunks(asins);
  const totalChunks = chunks.length;
  let uploadSessionId = null;
  const totals = {
    inserted: 0,
    skippedDuplicates: 0,
    skippedAlreadyInStore: 0,
  };

  for (let index = 0; index < chunks.length; index += 1) {
    const chunkIndex = index + 1;
    const isLastChunk = chunkIndex === totalChunks;
    const chunkAsins = chunks[index];

    const body = {
      asins: chunkAsins,
      categoryName,
      chunkIndex,
      totalChunks,
      isLastChunk,
    };
    if (uploadSessionId) {
      body.uploadSessionId = uploadSessionId;
    }

    let chunkAccepted = false;
    let lastError = null;

    for (let attempt = 0; attempt < UPLOAD_CHUNK_MAX_RETRIES; attempt += 1) {
      try {
        const data = await postChunk(body);
        if (data?.uploadSessionId) {
          uploadSessionId = data.uploadSessionId;
        }
        totals.inserted += Number(data?.insertedInChunk) || 0;
        totals.skippedDuplicates +=
          Number(data?.skippedDuplicatesInChunk) || 0;
        totals.skippedAlreadyInStore +=
          Number(data?.skippedAlreadyInStoreInChunk) || 0;

        onProgress?.({
          chunkIndex,
          totalChunks,
          uploadSessionId,
          receivedInChunk: Number(data?.receivedInChunk) || chunkAsins.length,
          percent: Math.round((chunkIndex / totalChunks) * 100),
          ...totals,
        });

        chunkAccepted = true;
        break;
      } catch (error) {
        lastError = error;
        const canRetry =
          isRetryableUploadError(error) &&
          attempt < UPLOAD_CHUNK_MAX_RETRIES - 1;
        if (!canRetry) {
          throw error;
        }
        await delay(UPLOAD_CHUNK_RETRY_DELAY_MS * (attempt + 1));
      }
    }

    if (!chunkAccepted && lastError) {
      throw lastError;
    }
  }

  return {
    uploadSessionId,
    totalAsins: asins.length,
    totalChunks,
    ...totals,
  };
}

export function formatBulkUploadSummary(result) {
  const parts = [
    `${result.totalAsins.toLocaleString("es-AR")} ASIN(s) procesados en ${result.totalChunks} lote(s).`,
    `${result.inserted.toLocaleString("es-AR")} encolados.`,
  ];
  if (result.skippedDuplicates > 0) {
    parts.push(
      `${result.skippedDuplicates.toLocaleString("es-AR")} duplicados omitidos.`
    );
  }
  if (result.skippedAlreadyInStore > 0) {
    parts.push(
      `${result.skippedAlreadyInStore.toLocaleString("es-AR")} ya publicados en tienda.`
    );
  }
  return parts.join(" ");
}
